/**
 * src/pages/FallacyLibrary/sections/IdentificationLab.tsx
 * Interactive Fallacy Identification Lab (id="identification-lab").
 *
 * Bank of 26 realistic fictional statements across all 7 categories.
 * Visitor classifies each statement as:
 * - Clear fallacy
 * - Weak inference
 * - Potentially reasonable argument
 * - Unsupported claim
 * - Ambiguous, needs more context
 *
 * For "Clear fallacy", the user selects which fallacy it is from 4 options.
 * Shuffles using seeded deterministic mulberry32 generator with incrementing seed counter.
 * Non-evaluative summary with gentle retry.
 */

import React, { useState, useMemo } from "react";
import type { LabStatement, StatementClassification } from "../types";
import { LAB_STATEMENTS } from "../data/labStatements";
import { ALL_FALLACIES } from "../data";
import { ToolFrame } from "@/components/knowledge/ToolFrame";
import { Feedback } from "@/components/knowledge/Feedback";
import { Link } from "@/components/common/Link";
import { RotateCcw, Sparkles, HelpCircle, ExternalLink, ChevronRight, RefreshCw } from "lucide-react";
import "@/components/cosmic/cosmic.css";
import "../fallacyLibrary.css";

// Seeded mulberry32 PRNG helper (no Math.random)
function mulberry32(a: number) {
  return function () {
    let t = a += 0x6d2b79f5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function seededShuffle<T>(array: T[], seed: number): T[] {
  const rng = mulberry32(seed);
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}
const CLASSIFICATION_OPTIONS: {
  id: StatementClassification;
  label: string;
  desc: string;
}[] = [{
  id: "clear-fallacy",
  label: "Clear Fallacy",
  desc: "Contains a distinct, recognizable structural or informal reasoning breakdown."
}, {
  id: "weak-inference",
  label: "Weak Inference",
  desc: "Premises offer some suggestive link, but the inductive leap is overextended."
}, {
  id: "reasonable-argument",
  label: "Potentially Reasonable Argument",
  desc: "Valid deductive form or robust, controlled empirical evidence supporting the claim."
}, {
  id: "unsupported-claim",
  label: "Unsupported Claim",
  desc: "A naked assertion lacking data, methodology, or evidence (neither valid nor tested)."
}, {
  id: "ambiguous-context",
  label: "Ambiguous, Needs More Context",
  desc: "Cannot be evaluated without knowing baseline risk, denominator, or sample parameters."
}];
export function IdentificationLab() {
  const [seed, setSeed] = useState<number>(1082);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [selectedClassification, setSelectedClassification] = useState<StatementClassification | null>(null);
  const [selectedFallacyId, setSelectedFallacyId] = useState<string | null>(null);
  const [hasEvaluated, setHasEvaluated] = useState<boolean>(false);

  // History of explored statements in this session for summary
  const [sessionHistory, setSessionHistory] = useState<{
    statementId: string;
    userClass: StatementClassification;
    correctClass: StatementClassification;
    category: string;
    matched: boolean;
  }[]>([]);

  // Seeded shuffled deck
  const shuffledStatements = useMemo(() => {
    return seededShuffle(LAB_STATEMENTS, seed);
  }, [seed]);
  const currentItem: LabStatement = shuffledStatements[currentIndex % shuffledStatements.length];
  const handleChooseClassification = (cls: StatementClassification) => {
    if (hasEvaluated) return;
    setSelectedClassification(cls);
    if (cls !== "clear-fallacy") {
      // If not a fallacy, evaluate immediately
      evaluateStep(cls, null);
    }
  };
  const handleChooseFallacy = (fallacyId: string) => {
    if (hasEvaluated) return;
    setSelectedFallacyId(fallacyId);
    evaluateStep("clear-fallacy", fallacyId);
  };
  const evaluateStep = (cls: StatementClassification, fallacyId: string | null) => {
    setHasEvaluated(true);
    const isClassMatch = cls === currentItem.classification;
    const isFallacyMatch = cls === "clear-fallacy" ? fallacyId === currentItem.associatedFallacyId : isClassMatch;
    setSessionHistory(prev => [...prev, {
      statementId: currentItem.id,
      userClass: cls,
      correctClass: currentItem.classification,
      category: currentItem.categoryTag,
      matched: isClassMatch && isFallacyMatch
    }]);
  };
  const handleNextStatement = () => {
    setSelectedClassification(null);
    setSelectedFallacyId(null);
    setHasEvaluated(false);
    setCurrentIndex(prev => (prev + 1) % shuffledStatements.length);
  };
  const handleNewOrder = () => {
    setSeed(prev => prev + 1);
    setCurrentIndex(0);
    setSelectedClassification(null);
    setSelectedFallacyId(null);
    setHasEvaluated(false);
  };
  const handleResetSession = () => {
    setSessionHistory([]);
    setCurrentIndex(0);
    setSelectedClassification(null);
    setSelectedFallacyId(null);
    setHasEvaluated(false);
  };

  // Linked fallacy entry if applicable
  const targetFallacy = currentItem.associatedFallacyId ? ALL_FALLACIES.find(f => f.id === currentItem.associatedFallacyId) : null;
  return <section id="identification-lab" className="w-full scroll-mt-28 my-10 min-w-0">
      <ToolFrame title="Fallacy Identification & Argument Classification Lab" purpose="Allows you to practice classifying real-world and scientific arguments as clear fallacies, weak inferences, reasonable arguments, unsupported claims, or ambiguous statements." limits="Categorization relies on human philosophical analysis and context evaluation. Statements in human discourse rarely come with neat labels; context and missing background variables often determine whether an inference holds.">
        <div className="p-4 sm:p-7 space-y-6">
          {/* Header Controls */}
          <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-border/40">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                Specimen {currentIndex + 1} of {shuffledStatements.length}
              </span>
              <span className="text-border">|</span>
              <span className="font-mono text-xs text-primary/80">
                Seed #{seed}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={handleNewOrder} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/60 bg-card/40 hover:bg-card/80 text-xs font-mono text-foreground transition-all cursor-pointer min-h-[38px]" title="Shuffle statements using seeded RNG">
                <RefreshCw className="size-3.5 text-primary" aria-hidden="true" />
                <span>New Order (Seed +1)</span>
              </button>

              {sessionHistory.length > 0 && <button type="button" onClick={handleResetSession} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/60 bg-card/40 hover:bg-card/80 text-xs font-mono text-muted-foreground hover:text-foreground transition-all cursor-pointer min-h-[38px]">
                  <RotateCcw className="size-3.5" aria-hidden="true" />
                  <span>Restart Lab</span>
                </button>}
            </div>
          </div>

          {/* Statement Display Card */}
          <div className="ql-glass p-5 sm:p-7 rounded-xl border border-border/80 bg-card/70">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="fl-telemetry-tag">
                Context: {currentItem.sourceContext}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground uppercase">
                {currentItem.categoryTag} Domain
              </span>
            </div>

            <blockquote className="font-sans text-base sm:text-lg text-foreground font-medium leading-relaxed my-2 italic border-l-2 border-primary pl-4">
              "{currentItem.statement}"
            </blockquote>
          </div>

          {/* Step 1: Broad Epistemic Classification */}
          <div>
            <h4 className="font-serif text-sm sm:text-base font-bold text-foreground mb-3 flex items-center gap-2">
              <HelpCircle className="size-4 text-primary shrink-0" aria-hidden="true" />
              <span>Step 1: Classify the Epistemic Status of this Statement</span>
            </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {CLASSIFICATION_OPTIONS.map(opt => {
              const isSelected = selectedClassification === opt.id;
              let btnStyle = "border-border/60 bg-card/40 hover:border-primary/50 text-foreground";
              if (hasEvaluated) {
                if (opt.id === currentItem.classification) {
                  btnStyle = "border-primary bg-primary/20 text-foreground shadow-[0_0_14px_rgba(34,211,238,0.2)]";
                } else if (isSelected && opt.id !== currentItem.classification) {
                  btnStyle = "border-destructive/80 bg-destructive/15 text-destructive-foreground";
                } else {
                  btnStyle = "opacity-40 border-border/30";
                }
              } else if (isSelected) {
                btnStyle = "border-primary bg-primary/15 text-primary";
              }
              return <button key={opt.id} type="button" onClick={() => handleChooseClassification(opt.id)} disabled={hasEvaluated} className={`p-3.5 rounded-lg border text-left transition-all flex flex-col justify-between min-h-[72px] cursor-pointer touch-manipulation focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 break-words ${btnStyle}`}>
                      <span className="font-serif text-sm font-bold block mb-1 break-words">
                        {opt.label}
                      </span>
                      <span className="font-sans text-xs text-muted-foreground leading-relaxed break-words">
                        {opt.desc}
                      </span>
                    </button>;
            })}
              </div>
          </div>

          {/* Step 2 (Conditional): If user selected Clear Fallacy, pick which one */}
          {selectedClassification === "clear-fallacy" && !hasEvaluated && currentItem.fallacyOptions && <div className="ql-glass p-5 rounded-xl border border-primary/40 bg-card/50">
              <h4 className="font-serif text-sm sm:text-base font-bold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="size-4 text-primary shrink-0" aria-hidden="true" />
                <span>Step 2: Identify the Specific Named Fallacy</span>
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentItem.fallacyOptions.map(fId => {
              const fMeta = ALL_FALLACIES.find(item => item.id === fId);
              const fName = fMeta ? fMeta.name : fId;
              return <button key={fId} type="button" onClick={() => handleChooseFallacy(fId)} className="p-3.5 rounded-lg border border-border/60 bg-card/60 hover:border-primary hover:bg-card/90 text-left font-serif text-xs sm:text-sm font-semibold text-foreground transition-all flex items-center justify-between gap-3 min-h-[48px] cursor-pointer touch-manipulation focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 break-words">
                      <span className="break-words">{fName}</span>
                      <ChevronRight className="size-4 text-primary shrink-0" aria-hidden="true" />
                    </button>;
            })}
              </div>
            </div>}

          {/* Evaluated Analysis and Feedback */}
          {hasEvaluated && <div className="space-y-4 pt-2">
              <Feedback tone={selectedClassification === currentItem.classification ? "good" : "partial"} title={selectedClassification === currentItem.classification ? "Accurate Epistemic Classification" : "Nuanced Analysis & Diagnostic Insight"}>
                <p className="mb-2">{currentItem.explanation}</p>
                <p className="text-xs text-muted-foreground">{currentItem.nuanceNotes}</p>
              </Feedback>

              {/* Link to full library entry if applicable */}
              {targetFallacy && <div className="flex items-center justify-between p-3.5 rounded-lg border border-border/60 bg-card/40 text-xs sm:text-sm">
                  <span className="font-sans text-muted-foreground">
                    Deepen your understanding in the Library:
                  </span>
                  <Link to={`#fallacy-${targetFallacy.id}`} className="inline-flex items-center gap-1.5 font-serif font-bold text-primary hover:underline">
                    <span>View {targetFallacy.name}</span>
                    <ExternalLink className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>}

              {/* Advance Button */}
              <div className="flex justify-end pt-3">
                <button type="button" onClick={handleNextStatement} className="ql-neon-btn px-6 py-2.5 rounded-full font-serif text-xs sm:text-sm font-bold tracking-wider uppercase cursor-pointer flex items-center gap-2">
                  <span>Next Specimen</span>
                  <ChevronRight className="size-4" aria-hidden="true" />
                </button>
              </div>
            </div>}

          {/* Non-Evaluative Diagnostic Reflection Summary */}
          {sessionHistory.length >= 3 && <div className="pt-6 border-t border-border/40">
              <h4 className="font-serif text-sm font-bold text-foreground uppercase tracking-wider mb-2">
                Session Inquiry Summary ({sessionHistory.length} explored)
              </h4>
              <p className="font-sans text-xs text-muted-foreground leading-relaxed mb-4">
                This is a non-evaluative pedagogical diagnostic. It illustrates which argument structures and inductive boundaries felt natural or required closer scrutiny:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="p-3 rounded-lg border border-border/40 bg-card/30">
                  <span className="font-mono text-xl font-bold text-primary block">
                    {sessionHistory.filter(s => s.matched).length}
                  </span>
                  <span className="font-sans text-[11px] text-muted-foreground uppercase">
                    Aligned Inquiries
                  </span>
                </div>

                <div className="p-3 rounded-lg border border-border/40 bg-card/30">
                  <span className="font-mono text-xl font-bold text-amber-400 block">
                    {sessionHistory.filter(s => !s.matched).length}
                  </span>
                  <span className="font-sans text-[11px] text-muted-foreground uppercase">
                    Nuanced Boundaries
                  </span>
                </div>

                <div className="p-3 rounded-lg border border-border/40 bg-card/30">
                  <span className="font-mono text-xl font-bold text-foreground block">
                    {new Set(sessionHistory.map(s => s.category)).size}
                  </span>
                  <span className="font-sans text-[11px] text-muted-foreground uppercase">
                    Categories Covered
                  </span>
                </div>

                <div className="p-3 rounded-lg border border-border/40 bg-card/30">
                  <span className="font-mono text-xl font-bold text-muted-foreground block">
                    {shuffledStatements.length - (currentIndex + 1)}
                  </span>
                  <span className="font-sans text-[11px] text-muted-foreground uppercase">
                    Remaining in Deck
                  </span>
                </div>
              </div>
            </div>}
        </div>
      </ToolFrame>
    </section>;
}